import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';

import { getFieldOptions } from '../../utils/ResourceFieldOption';
import {
  InputUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  TimePickerUi,
  FormErrorUI,
  FormLabelUI,
  PhoneInputUI,
  DateTimePickerUI,
  AddButtonUi,
} from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import { copyDynVals, emptyDynFieldsAfterCopy } from '../../utils/DynHelper';
import useMenus from '../../context/MenuContext';
import dayjs from 'dayjs';
import useUsers from '../../context/UsersContext';

const FormFields = ({
  viewField,
  selectedValues,
  params,
  submenuNames,
  autocompleteOptions,
  roles,
  editButtonVisible,
  autocompleteData,
  dynProperties,
  setProjectDialogOpen,
}) => {
  const { user } = useUsers();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { dynProps } = dynProperties;
  const { getMenuSubMenuId } = useMenus();
  const [projectOptions, setProjectOptions] = useState(autocompleteOptions?.project ?? []);

  const onlyOneResourceSelect = params.SIMPLETASK_ONLY_ONE_RESOURCE_SELECT;
  const resourceIdFields = ['resourceOneId', 'resourceTwoId', 'resourceThreeId', 'resourceFourId'];

  const isAnyFieldFilled = () => onlyOneResourceSelect === 'Y' && resourceIdFields.some((key) => !!values[key]);

  const projectSelectFirst = params.SIMPLETASK_PROJECT_FIRST;
  const disabledField = !values.projectId && projectSelectFirst;

  const fieldOptionsResourceOne = getFieldOptions('resourceOne', projectSelectFirst, disabledField, values.projectId);
  const fieldOptionsResourceTwo = getFieldOptions('resourceTwo', projectSelectFirst, disabledField, values.projectId);
  const fieldOptionsResourceThree = getFieldOptions(
    'resourceThree',
    projectSelectFirst,
    disabledField,
    values.projectId
  );
  const fieldOptionsResourceFour = getFieldOptions('resourceFour', projectSelectFirst, disabledField, values.projectId);

  const handleGenerateStatusOption = () => {
    const filteredStatus = autocompleteData?.status?.statusItems?.filter((item) => item.onKanban);
    const generatedStatus = generateAutocompleteItems(filteredStatus, 'itemName', 'itemId');
    return generatedStatus;
  };

  useEffect(() => {
    if (!selectedValues.simpleTaskId && values.resourceFourId && params.SIMPLETASK_RESOURCEFOUR_TASK_TYPE) {
      setFieldValue('simpleTaskTypeId', parseInt(params.SIMPLETASK_RESOURCEFOUR_TASK_TYPE));
    }
  }, [values.resourceFourId]);

  useEffect(() => {
    const classificationParam = params.SIMPLETASK_SHOW_PROJECT_BY_CLASSIFICATION_ID;

    if (!classificationParam || selectedValues.simpleTaskId) {
      setProjectOptions(autocompleteOptions?.project ?? []);
      return;
    }

    const classificationId = parseInt(classificationParam);

    const filteredProjects = autocompleteData?.project?.filter(
      (p) => p.classificationId === classificationId && p.active
    );
    const generated = generateAutocompleteItems(filteredProjects, ['projectName', 'orderNumber'], 'projectId');

    setProjectOptions(generated);
  }, [params, autocompleteOptions.project, selectedValues]);

  useEffect(() => {
    calcDaysNumber();
  }, [values.startDate, values.endDate, params]);

  const calcDaysNumber = () => {
    if (params.SIMPLETASK_DAYS_NUMBER_FIELD !== 'Y') return;

    if (values.startDate && values.endDate) {
      const start = dayjs(values.startDate).startOf('day');
      const end = dayjs(values.endDate).startOf('day');
      const diffDays = end.diff(start, 'day') + 1;

      setFieldValue('daysNumber', diffDays > 0 ? diffDays : 0);
    } else {
      setFieldValue('daysNumber', '');
    }
  };

  const hasEditDateFields = params.SIMPLETASK_DATES_ONLY_EDIT_BY_OWNER === 'Y' && values.userId !== user.userId;

  const quantityUnitCode =
    selectedValues?.quantityUnit?.code ||
    autocompleteData?.quantityUnit?.find((unit) => unit.id === values.quantityUnitId)?.code;

  const copyTime = (targetDate, sourceDate) =>
    dayjs(targetDate)
      .hour(dayjs(sourceDate).hour())
      .minute(dayjs(sourceDate).minute())
      .second(dayjs(sourceDate).second());

  const handleStartDateChange = (newStartDate) => {
    if (!newStartDate) {
      setFieldValue('startDate', null);
      return;
    }

    const updatedStartDate = dayjs(newStartDate);

    if (values.endDate && updatedStartDate.isAfter(dayjs(values.endDate), 'day')) {
      setFieldValue('endDate', copyTime(updatedStartDate, values.endDate));
    }

    setFieldValue('startDate', updatedStartDate).then(() => {
      validateField('startDate');
      validateField('endDate');
    });
  };

  const handleEndDateChange = (newEndDate) => {
    if (!newEndDate) {
      setFieldValue('endDate', null);
      return;
    }

    const updatedEndDate = dayjs(newEndDate);

    if (values.startDate && updatedEndDate.isBefore(dayjs(values.startDate), 'day')) {
      setFieldValue('startDate', copyTime(updatedEndDate, values.startDate));
    }

    setFieldValue('endDate', updatedEndDate).then(() => {
      validateField('endDate');
      validateField('startDate');
    });
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-2 md:mx-4">
      <div className="col-span-4 ">
        <Field
          type="text"
          name="simpleTaskName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Feladat rövid neve" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.simpleTaskName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.simpleTaskName !== '' }}
          inputProps={{
            maxLength: 300,
          }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="startDate"
          component={DatePickerUI}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={handleStartDateChange}
          disabled={viewField || hasEditDateFields}
          InputLabelProps={{ shrink: values.startDate !== '' }}
        />
      </div>
      {params.HIDE_SIMPLETASK_STARTTIME !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="time"
            name="startDate"
            component={TimePickerUi}
            value={values.startDate}
            fullWidth
            label={<FormLabelUI text="Kezdő időpont" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.startDate} />}
            onChange={handleStartDateChange}
            disabled={viewField || hasEditDateFields}
            InputLabelProps={{ shrink: values.startDate !== '' }}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_ENDDATE !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="date"
            name="endDate"
            component={DatePickerUI}
            value={values.endDate}
            fullWidth
            label={<FormLabelUI text="Befejező dátum" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.endDate} />}
            onChange={handleEndDateChange}
            disabled={viewField || hasEditDateFields}
            InputLabelProps={{ shrink: values.endDate !== '' }}
          />
        </div>
      )}
      {params.SIMPLETASK_DAYS_NUMBER_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="daysNumber"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Napok száma" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.daysNumber} />}
            disabled
            InputLabelProps={{ shrink: values.daysNumber && values.daysNumber !== '' }}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_DURATION !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="duration"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Időintervallum" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.duration} />}
            disabled={viewField || hasEditDateFields}
            InputLabelProps={{ shrink: values.duration !== '' }}
            onChange={(e) => {
              const newendDate = values.startDate.add(e.target.value, 'hour');
              setFieldValue('duration', e.target.value ?? '');
              setFieldValue('endDate', newendDate).then(() => {
                validateField('endDate');
              });
            }}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_PLANNED_TIME !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="plannedTime"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text={`Tervezett idő ${quantityUnitCode ? `(${quantityUnitCode})` : ''}`} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.plannedTime} />}
            disabled={viewField || hasEditDateFields}
            InputLabelProps={{ shrink: values.plannedTime && values.plannedTime !== '' }}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_PREPAREDNESS !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="preparedness"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Készültség" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.preparedness} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.preparedness !== '' }}
          />
        </div>
      )}
      {params.SIMPLETASK_RESOURCE_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="machineId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Erőforrás" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.machineId} />}
            onChange={(_e, newVal) => {
              setFieldValue('machineId', newVal?.value ?? null).then(() => {
                validateField('machineId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.machineId !== '' }}
            selectedValue={values.machineId}
            selectedLabelValue={selectedValues?.machine?.machineName}
            dataset={autocompleteOptions?.machine}
          />
        </div>
      )}
      {params.SIMPLETASK_PROJECT_FIELD === 'Y' && (
        <>
          <div className="col-span-4 md:col-span-1">
            <div className="flex items-center">
              <Field
                type="autocomplete"
                name="projectId"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Projekt" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.projectId} />}
                onChange={(_e, newVal) => {
                  // setelni a dinamikus tulajdonsagokat is, és a projekt orderNumber
                  const findedProject = autocompleteData?.project.find((item) => item.projectId === newVal?.value);
                  if (findedProject) {
                    setFieldValue('projectOrderNumber', findedProject.orderNumber || '');
                    copyDynVals(dynProps, findedProject.dynamicProperties, setFieldValue);
                  } else {
                    setFieldValue('projectOrderNumber', '');
                    const submenuId = getMenuSubMenuId('Project');
                    emptyDynFieldsAfterCopy(dynProps, submenuId, setFieldValue);
                  }

                  setFieldValue('projectId', newVal?.value ?? null).then(() => {
                    validateField('projectId');
                  });
                }}
                disabled={viewField}
                InputLabelProps={{ shrink: values.projectId !== '' }}
                selectedValue={values.projectId}
                selectedLabelValue={selectedValues?.project?.projectName}
                dataset={projectOptions}
              />
              <AddButtonUi onClick={() => setProjectDialogOpen(true)} disabled={viewField} />
            </div>
          </div>
          {params.HIDE_SIMPLETASK_PROJECT_ORDERNUMBER !== 'Y' && (
            <div className="col-span-4 md:col-span-1">
              <Field
                type="autocomplete"
                name="projectOrderNumber"
                component={InputUI}
                fullWidth
                label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
                variant="standard"
                helperText={<FormErrorUI message={errors.projectOrderNumber} />}
                disabled={true}
                InputLabelProps={{ shrink: values.projectOrderNumber !== '' }}
                value={values.projectOrderNumber}
              />
            </div>
          )}
        </>
      )}
      {params.SIMPLETASK_RESOURCE_ONE_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="resourceOneId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text={submenuNames[0]} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.resourceOneId} />}
            onChange={(_e, newVal) => {
              setFieldValue('resourceOneId', newVal?.value ?? null).then(() => {
                validateField('resourceOneId');
              });
            }}
            disabled={viewField || (isAnyFieldFilled() && !values.resourceOneId) || disabledField}
            InputLabelProps={{ shrink: values.resourceOneId !== '' }}
            selectedValue={values.resourceOneId}
            selectedLabelValue={selectedValues?.resourceOne?.name}
            {...fieldOptionsResourceOne}
          />
        </div>
      )}
      {params.SIMPLETASK_RESOURCE_TWO_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="resourceTwoId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text={submenuNames[1]} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.resourceTwoId} />}
            onChange={(_e, newVal) => {
              setFieldValue('resourceTwoId', newVal?.value ?? null).then(() => {
                validateField('resourceTwoId');
              });
            }}
            disabled={viewField || (isAnyFieldFilled() && !values.resourceTwoId) || disabledField}
            InputLabelProps={{ shrink: values.resourceTwoId !== '' }}
            selectedValue={values.resourceTwoId}
            selectedLabelValue={selectedValues?.resourceTwo?.name}
            {...fieldOptionsResourceTwo}
          />
        </div>
      )}
      {params.SIMPLETASK_RESOURCE_THREE_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="resourceThreeId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text={submenuNames[2]} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.resourceThreeId} />}
            onChange={(_e, newVal) => {
              setFieldValue('resourceThreeId', newVal?.value ?? null).then(() => {
                validateField('resourceThreeId');
              });
            }}
            disabled={viewField || (isAnyFieldFilled() && !values.resourceThreeId) || disabledField}
            InputLabelProps={{ shrink: values.resourceThreeId !== '' }}
            selectedValue={values.resourceThreeId}
            selectedLabelValue={selectedValues?.resourceThree?.name}
            {...fieldOptionsResourceThree}
          />
        </div>
      )}
      {params.SIMPLETASK_RESOURCE_FOUR_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="resourceFourId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text={submenuNames[3]} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.resourceFourId} />}
            onChange={(_e, newVal) => {
              setFieldValue('resourceFourId', newVal?.value ?? null).then(() => {
                validateField('resourceFourId');
              });
            }}
            disabled={viewField || (isAnyFieldFilled() && !values.resourceFourId) || disabledField}
            InputLabelProps={{ shrink: values.resourceFourId !== '' }}
            selectedValue={values.resourceFourId}
            selectedLabelValue={selectedValues?.resourceFour?.name}
            {...fieldOptionsResourceFour}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_COMPANY !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="companyId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Cég" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyId} />}
            onChange={(_e, newVal) => {
              setFieldValue('companyContactId', null);

              setFieldValue('companyId', newVal?.value ?? null).then(() => {
                validateField('companyId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyId !== '' }}
            selectedValue={values.companyId}
            selectedLabelValue={selectedValues?.company?.name}
            dataset={autocompleteOptions.company}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_TASKTYPE !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="simpleTaskTypeId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Feladat típusa" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.simpleTaskTypeId} />}
            onChange={(_e, newVal) => {
              setFieldValue('simpleTaskTypeId', newVal?.value ?? null).then(() => {
                validateField('simpleTaskTypeId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.simpleTaskTypeId !== '' }}
            selectedValue={values.simpleTaskTypeId}
            selectedLabelValue={selectedValues?.taskType?.itemName}
            dataset={autocompleteOptions?.type}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_OWNER !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="userId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Tulajdonos" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.userId} />}
            onChange={(_e, newVal) => {
              setFieldValue('userId', newVal?.value ?? null).then(() => {
                validateField('userId');
              });
            }}
            disabled
            InputLabelProps={{ shrink: values.userId !== '' }}
            selectedValue={values.userId}
            selectedLabelValue={selectedValues?.user?.userName}
            dataset={autocompleteOptions?.user}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_RESP_PERSON !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="resposiblePersonId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Felelős" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.resposiblePersonId} />}
            onChange={(_e, newVal) => {
              setFieldValue('resposiblePersonId', newVal?.value ?? null).then(() => {
                validateField('resposiblePersonId');
              });
            }}
            disabled={viewField || (selectedValues.simpleTaskId && roles.SIMPLETASK_NOT_EDIT_RESPONSIBLE_FIELD)}
            InputLabelProps={{ shrink: values.resposiblePersonId !== '' }}
            selectedValue={values.resposiblePersonId}
            selectedLabelValue={selectedValues?.responsiblePerson?.userName}
            dataset={autocompleteOptions?.user}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_STATUS !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="statusId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Státusz" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.statusId} />}
            onChange={(_e, newVal) => {
              setFieldValue('statusId', newVal?.value ?? null).then(() => {
                validateField('statusId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.statusId !== '' }}
            selectedValue={values.statusId}
            selectedLabelValue={selectedValues?.status?.itemName}
            dataset={
              !editButtonVisible && roles.TASK_KANBAN_ONLY_KANBAN_STATUS_SELECT
                ? handleGenerateStatusOption()
                : autocompleteOptions?.status
            }
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_PRIORITY !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="priorId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Prioritás" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.priorId} />}
            onChange={(_e, newVal) => {
              setFieldValue('priorId', newVal?.value ?? null).then(() => {
                validateField('priorId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.priorId !== '' }}
            selectedValue={values.priorId}
            selectedLabelValue={selectedValues?.priority?.itemName}
            dataset={autocompleteOptions?.priority}
          />
        </div>
      )}
      {params.SIMPLETASK_COMPANY_LOCATION_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="companyLocationId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Telephely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyLocationId} />}
            onChange={(_e, newVal) => {
              setFieldValue('companyLocationId', newVal?.value ?? null).then(() => {
                validateField('companyLocationId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyLocationId !== '' }}
            selectedValue={values.companyLocationId}
            selectedLabelValue={selectedValues?.companyLocation?.name}
            dataset={autocompleteOptions?.companylocation}
          />
        </div>
      )}
      {params.HIDE_SIMPLETASK_PHONENUMBER !== 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="phoneNumber"
            as={PhoneInputUI}
            fullWidth
            label={<FormLabelUI text="Telefonszám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.phoneNumber} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.phoneNumber !== '' }}
          />
        </div>
      )}
      {params.SHOW_SIMPLETASK_JOKER_CHAR_HEAD1 === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="text"
            name="joker_char_head1"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text={params?.SIMPLETASK_JOKER_CHAR_HEAD1_LABEL ?? 'Egyéb'} />}
            variant="standard"
            helperText={<FormErrorUI message={errors.joker_char_head1} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.joker_char_head1 !== '' }}
          />
        </div>
      )}
      {params.SHOW_SIMPLETASK_FINISHED_AT_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="date"
            name="finishedAt"
            component={DateTimePickerUI}
            value={values.finishedAt}
            fullWidth
            label={<FormLabelUI text="Befejezés időpontja" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.finishedAt} />}
            onChange={(date) => {
              setFieldValue('finishedAt', date).then(() => {
                validateField('finishedAt');
              });
            }}
            disabled
            InputLabelProps={{ shrink: values.finishedAt !== '' }}
          />
        </div>
      )}
    </div>
  );
};

export default FormFields;
